import React, { useMemo, useState, useRef } from "react";
import { gsap } from "gsap";
import styled from "styled-components";

import { autofillData } from "./data";
import type {
  AutofillContent,
  AutofillGroup,
  AutofillItem,
} from "./data";

// Styled components
const TagContainer = styled.div<{ $nowrap: boolean }>`
  display: flex;
  flex-wrap: ${({ $nowrap }) => ($nowrap ? "nowrap" : "wrap")};
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
`;

const Tag = styled.span.attrs({
  className: "bodytext-13",
})`
  position: relative;
  margin: 4px 4px 4px 2px;
  padding: 4px 12px;
  border-radius: 4px;
  color: var(--soremo-light-gray);
  background-color: var(--soremo-background);
  outline: 1px solid var(--soremo-border);
  user-select: none;
  cursor: pointer;
  overflow: hidden;

  flex-shrink: 0;

  .shine {
    position: absolute;
    top: 0;
    left: -24px;
    width: 16px;
    height: 100%;
    background: rgba(255, 255, 255, 0.75);
    transform: skewX(-15deg);
    pointer-events: none;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: linear-gradient(
        105deg,
        #feffc7 0%,
        rgba(255, 255, 255, 0.5) 100%
      );
    }
  }
`;

interface TagComponentProps {
  onClick: () => void;
  children: React.ReactNode;
}

const TagComponent = React.forwardRef<HTMLSpanElement, TagComponentProps>(
  ({ onClick, children }, ref) => {
    return (
      <Tag onClick={onClick} ref={ref}>
        {children}
        <span className="shine" />
      </Tag>
    );
  },
);
TagComponent.displayName = "TagComponent";

export type { AutofillGroup };

/** 文字列コンテンツはそのまま、関数コンテンツは実行して文字列に解決する。 */
const resolveContent = (content: AutofillContent): string =>
  typeof content === "function" ? content() : content;

export interface AutofillProps {
  group: AutofillGroup;
  nowrap?: boolean;
  /** タグタップ時に、その keyword に対応する定型文（ランダム選択）を渡す。 */
  onSelectContent?: (content: string) => void;
  /**
   * タグタップ時に、タップされた keyword（テーマ名）そのものを渡す。
   * 定型文ではなく「テーマ」が欲しい用途（AI 生成のテーマ指定など）で使う。
   * onSelectContent と併用も可能。
   */
  onSelectKeyword?: (keyword: string) => void;
}

const Autofill: React.FC<AutofillProps> = ({
  group,
  nowrap = false,
  onSelectContent,
  onSelectKeyword,
}) => {
  const [previousContent, setPreviousContent] = useState<string | null>(null);

  // group に対応する定型文セットは静的レジストリから同期取得する。
  const dataArray: AutofillItem[] = autofillData[group] ?? [];
  const keywords = useMemo(
    () => dataArray.map((item) => item.keyword),
    [dataArray],
  );

  // タグの参照を保持する配列
  const tagRefs = useRef<Array<HTMLSpanElement | null>>([]);

  const handleKeywordClick = (keyword: string, index: number) => {
    animateShine(tagRefs.current[index]);

    // テーマ名そのものが欲しい用途（AI 生成など）向けに、まず keyword を通知する。
    if (onSelectKeyword) {
      onSelectKeyword(keyword);
    }

    const matchedItem = dataArray.find((item) => item.keyword === keyword);

    if (matchedItem && Array.isArray(matchedItem.contents)) {
      let availableContents = matchedItem.contents;

      // データが複数ある場合、前回のデータを除外
      if (availableContents.length > 1 && previousContent !== null) {
        availableContents = availableContents.filter(
          (content) => resolveContent(content) !== previousContent,
        );
      }

      // ランダムに一つのデータを選択（関数コンテンツはここで実行して文字列化）
      const picked =
        availableContents[Math.floor(Math.random() * availableContents.length)];
      const value = resolveContent(picked);

      // 前回選択したデータとして保存
      setPreviousContent(value);

      // 親コンポーネントのコールバック関数を呼び出し、選択したデータを渡す
      if (onSelectContent) {
        onSelectContent(value);
      }
    }
  };

  const animateShine = (tagElement: HTMLSpanElement | null) => {
    if (!tagElement) return;
    const shineElement = tagElement.querySelector(".shine");
    if (!shineElement) return;
    gsap.fromTo(
      shineElement,
      { left: "-24px" },
      {
        left: "100%",
        duration: 0.3,
        ease: "power2.inOut",
        onComplete: () => {
          // アニメーション後に初期位置に戻す
          gsap.set(shineElement, { left: "-24px" });
        },
      },
    );
  };

  return (
    <TagContainer $nowrap={nowrap}>
      {keywords.map((keyword, index) => (
        <TagComponent
          key={index}
          onClick={() => handleKeywordClick(keyword, index)}
          ref={(el) => {
            tagRefs.current[index] = el;
          }}
        >
          {keyword}
        </TagComponent>
      ))}
    </TagContainer>
  );
};

export default Autofill;
